import React, { useCallback, useContext, useMemo, useState } from 'react';
// Hooks
import { useCreateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useCreateResource';
import { useDeleteResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useDeleteResource';
import { useMutateResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useMutateResource';
// Types
import { Comment } from '@cube3/state/types';
// Context
import { ReviewViewContext } from '../../ReviewTool/Context/reviewViewContext';
// Helpers
import { getCommentResolution } from '../utils/getCommentResolution';
// UI
import CommentTile, {
  useCommentStyles
} from '@cube3/cubicle/src/core/molecules/ReviewTools/Comment';
import { Divider } from '@cube3/cubicle/src/core/atoms/Divider/Divider';
import { ReviewToolContext } from '../Context/reviewToolContext';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { ReplyField } from '@cube3/cubicle/src/core/molecules/ReviewTools/ReplyField';

interface Props {
  comment: Comment;
  assetId: string;
}

/** Component to list each Comment and its replies */
export const ReviewComment: React.FC<Props> = React.memo((props) => {
  const { comment, assetId } = props;

  const { email, reviewLink } = useContext(ReviewViewContext);
  const { player, activeComment, toggleDrawingBar, setActiveComment } =
    useContext(ReviewToolContext);

  const { resources: subComments } = useResourceList__ALPHA(
    useMemo(
      () => ({
        resourceType: 'content-tree-node',
        resourceId: assetId,
        edgeType: 'comment',
        edgeLabel: 'comments',
        params: {
          filter: {
            thread: comment?.id
          }
        }
      }),
      [assetId, comment]
    )
  );

  const cacheInvalidator = useCallback(
    (r) => [
      r,
      { type: 'content-tree-node', id: assetId, relationship: 'comments' },
      { type: 'share', id: reviewLink?.id, relationship: 'activities' }
    ],
    [assetId, reviewLink]
  );

  const [deleteResource] = useDeleteResource__ALPHA({
    cacheInvalidator
  });
  const [createComment] = useCreateResource__ALPHA({
    resourceType: 'comment',
    cacheInvalidator
  });
  const [mutateResource] = useMutateResource__ALPHA({
    cacheInvalidator
  });

  const resolution = getCommentResolution(subComments);
  const resolved = resolution === 'resolve';

  const replies = useMemo(() => {
    if (!subComments?.length) return [];
    const repliesOnly = subComments.filter((co) => !co.resolution);
    return [...repliesOnly].sort(
      (a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }, [subComments]);

  const onEditMessage = useCallback(
    (id: string) => {
      return (message: string) => {
        if (message) {
          mutateResource({
            type: 'comment',
            id: id,
            message: message
          });
        }
      };
    },
    [mutateResource]
  );

  const onResolve = useCallback(() => {
    createComment({
      type: 'comment',
      resolution: resolved ? 'unresolve' : 'resolve',
      relationships: {
        thread: {
          data: {
            type: 'comment',
            id: comment.id
          }
        }
      }
    });
  }, [createComment, resolved]);

  const onReply = useCallback(
    (message: string) => {
      createComment({
        type: 'comment',
        message,
        relationships: {
          thread: {
            data: {
              type: 'comment',
              id: comment.id
            }
          }
        }
      });
    },
    [createComment, assetId, comment]
  );

  const onDelete = useCallback(
    (id: string) => {
      return () => {
        setActiveComment(undefined);
        deleteResource({
          type: 'comment',
          id
        });
      };
    },
    [deleteResource]
  );
  /** click comment to
   * - reset toggle drawing bar to false
   * - if the comment is created at certain frame/played time, should navigate the video player to that time
   */
  const onClickComment = useCallback(
    (event) => {
      event.stopPropagation();

      if (player) {
        player.playPause(false);
        if (player && comment.start !== undefined) {
          player.goToTime(comment.start);
        }
      }
      setActiveComment(comment);
      toggleDrawingBar(false); // hide drawing bar

    },
    [comment, player, toggleDrawingBar, setActiveComment]
  );

  const active = activeComment?.id === comment.id;
  const classes = useCommentStyles({ active });


  const [hovering, setHover] = useState(false);

  return comment ? (
    <div
      data-set="preventDeselect"
      className={classes.container}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-pw={`review-commment-${comment?.message}`}
    >
      <CommentTile
        comment={comment}
        active={active}
        resolved={resolved}
        canEdit={email === comment?.author_name}
        onClick={onClickComment}
        onResolve={onResolve}
        onDelete={onDelete(comment.id)}
        onEdit={onEditMessage(comment.id)}
        hovering={hovering}
      />
      {replies?.length ? (
        <React.Fragment data-pw="review-replies">
          {replies.map((item, idx) => (
            <React.Fragment
              key={item.id + idx}
              data-pw={`review-reply-${item.message}`}
            >
              <CommentTile
                comment={item}
                active={active}
                showTimestamp={false}
                canEdit={email === item.author_name}
                onDelete={onDelete(item.id)}
                onClick={onClickComment}
                onEdit={onEditMessage(item.id)}
                hovering={hovering}
              />
            </React.Fragment>
          ))}
        </React.Fragment>
      ) : null}

      {active && (
        <div className={classes.root}>
          <Divider color="line3" />
          <ReplyField onSave={onReply} />
        </div>
      )}
    </div>
  ) : null;
});
